import createClient from 'openapi-fetch';

import type { paths } from './schema.gen';
import { ApiError, NetworkError, toApiError } from './errors';
import { tokenStore } from './token-store';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '';

const LOCK = 'hp-auth-refresh';

// Endpoints a signed-out visitor calls: their 401 is an answer (wrong password), not an expired session.
const ANONYMOUS =
  /\/v1\/auth\/(login|refresh|register|confirm|forgot-password|reset-password|undo-email-change)(\?|$)/;

type TRefresh = 'refreshed' | 'signed-out' | 'failed';

const callRefresh = async (stale: string | null): Promise<TRefresh> => {
  tokenStore.reload();
  const current = tokenStore.access();
  if (current && current !== stale && tokenStore.fresh(current)) return 'refreshed';

  const refreshToken = tokenStore.refresh();
  if (!refreshToken) return 'signed-out';

  let response: Response;
  try {
    response = await fetch(`${BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    return 'failed';
  }
  if (response.status >= 500) return 'failed';
  if (!response.ok) return 'signed-out';

  const body = (await response.json()) as { accessToken: string; refreshToken: string };
  tokenStore.set(body.accessToken, body.refreshToken);
  return 'refreshed';
};

// One refresh per browser (the server reads a second use as theft); a waiter re-reads storage first.
const withLock = async (task: () => Promise<TRefresh>): Promise<TRefresh> =>
  typeof navigator !== 'undefined' && 'locks' in navigator
    ? await navigator.locks.request(LOCK, task)
    : task();

let refreshing: Promise<TRefresh> | null = null;

const refreshOnce = (stale: string | null): Promise<TRefresh> => {
  refreshing ??= withLock(() => callRefresh(stale))
    .then((outcome) => {
      if (outcome === 'signed-out') tokenStore.expire();
      return outcome;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
};

export const api = createClient<paths>({ baseUrl: BASE });

// The send consumes a body stream; a retry built from the sent request fails as "never left".
const unsent = new WeakMap<Request, { copy: Request; token: string | null }>();

api.use({
  onRequest({ request }) {
    const token = tokenStore.access();
    if (token) request.headers.set('authorization', `Bearer ${token}`);
    unsent.set(request, { copy: request.clone(), token });
    return request;
  },

  async onResponse({ request, response }) {
    const sent = unsent.get(request);
    unsent.delete(request);
    if (response.status !== 401 || ANONYMOUS.test(new URL(request.url).pathname)) return response;
    if (!sent || (await refreshOnce(sent.token)) !== 'refreshed') return response;

    sent.copy.headers.set('authorization', `Bearer ${tokenStore.access() ?? ''}`);
    const retried = await fetch(sent.copy);
    if (retried.status === 401) tokenStore.expire();
    return retried;
  },
});

export const unwrap = async <T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> => {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch (cause) {
    throw new NetworkError(cause);
  }

  if (result.error !== undefined || !result.response.ok) {
    throw toApiError(result.error, result.response);
  }
  return result.data as T;
};

export { ApiError, NetworkError };
